import React from 'react';
import PropTypes from 'prop-types';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { SURVEY_INPUT_PROCESSING_STATES, READABLE_SURVEY_INPUT_PROCESSING_STATES } from '../../Constants';
import './StatusSummary.scss';

// Define workflow order for status display
const STATUS_WORKFLOW_ORDER = [
    SURVEY_INPUT_PROCESSING_STATES.submitted,
    SURVEY_INPUT_PROCESSING_STATES.pendingLeadReview,
    SURVEY_INPUT_PROCESSING_STATES.readyForReview,
    SURVEY_INPUT_PROCESSING_STATES.inReview,
    SURVEY_INPUT_PROCESSING_STATES.readyForAdminQC,
    SURVEY_INPUT_PROCESSING_STATES.flaggedForAdminReview,
    SURVEY_INPUT_PROCESSING_STATES.flaggedForLeadReview,
    SURVEY_INPUT_PROCESSING_STATES.awaitingClientResponse,
    SURVEY_INPUT_PROCESSING_STATES.reviewComplete,
    SURVEY_INPUT_PROCESSING_STATES.accepted
];

// Status descriptions for tooltips
const STATUS_DESCRIPTIONS = {
    [SURVEY_INPUT_PROCESSING_STATES.submitted]: 'Initial submission, pending review assignment',
    [SURVEY_INPUT_PROCESSING_STATES.pendingLeadReview]: 'Awaiting lead reviewer assessment',
    [SURVEY_INPUT_PROCESSING_STATES.readyForReview]: 'Ready for detailed review process',
    [SURVEY_INPUT_PROCESSING_STATES.inReview]: 'Currently under active review',
    [SURVEY_INPUT_PROCESSING_STATES.flaggedForAdminReview]: 'Requires administrator attention',
    [SURVEY_INPUT_PROCESSING_STATES.flaggedForLeadReview]: 'Requires lead reviewer attention',
    [SURVEY_INPUT_PROCESSING_STATES.awaitingClientResponse]: 'Waiting for client feedback',
    [SURVEY_INPUT_PROCESSING_STATES.readyForAdminQC]: 'Ready for final quality check',
    [SURVEY_INPUT_PROCESSING_STATES.reviewComplete]: 'Review process completed',
    [SURVEY_INPUT_PROCESSING_STATES.accepted]: 'Survey was accepted by the FWC Admin'
};

const StatusSummary = ({ data = [], loading = false }) => {
    const getStatusCounts = () => {
        const counts = {
            [SURVEY_INPUT_PROCESSING_STATES.submitted]: 0,
            [SURVEY_INPUT_PROCESSING_STATES.pendingLeadReview]: 0,
            [SURVEY_INPUT_PROCESSING_STATES.readyForReview]: 0,
            [SURVEY_INPUT_PROCESSING_STATES.inReview]: 0,
            [SURVEY_INPUT_PROCESSING_STATES.flaggedForAdminReview]: 0,
            [SURVEY_INPUT_PROCESSING_STATES.flaggedForLeadReview]: 0,
            [SURVEY_INPUT_PROCESSING_STATES.awaitingClientResponse]: 0,
            [SURVEY_INPUT_PROCESSING_STATES.readyForAdminQC]: 0,
            [SURVEY_INPUT_PROCESSING_STATES.reviewComplete]: 0,
            [SURVEY_INPUT_PROCESSING_STATES.accepted]: 0
        };

        if (!data || !Array.isArray(data)) return counts;

        data.forEach(record => {
            if (record?.processingStatus) {
                counts[record.processingStatus] = (counts[record.processingStatus] || 0) + 1;
            }
        });

        return counts;
    };

    const statusCounts = getStatusCounts();
    const totalRecords = data?.length || 0;

    const getStatusPercentage = (count) => {
        return totalRecords > 0 ? Math.round((count / totalRecords) * 100) : 0;
    };

    const getStatusGroup = (status) => {
        switch (status) {
            case SURVEY_INPUT_PROCESSING_STATES.submitted:
            case SURVEY_INPUT_PROCESSING_STATES.pendingLeadReview:
            case SURVEY_INPUT_PROCESSING_STATES.awaitingClientResponse:
                return 'pending';
            case SURVEY_INPUT_PROCESSING_STATES.readyForReview:
            case SURVEY_INPUT_PROCESSING_STATES.inReview:
            case SURVEY_INPUT_PROCESSING_STATES.readyForAdminQC:
                return 'in-progress';
            case SURVEY_INPUT_PROCESSING_STATES.flaggedForAdminReview:
            case SURVEY_INPUT_PROCESSING_STATES.flaggedForLeadReview:
                return 'flagged';
            case SURVEY_INPUT_PROCESSING_STATES.reviewComplete:
            case SURVEY_INPUT_PROCESSING_STATES.accepted:
                return 'completed';
            default:
                return 'unknown';
        }
    };

    const renderStatusCard = (status) => {
        if (loading) {
            return (
                <output 
                    key={status}
                    className={`status-summary-item status-${getStatusGroup(status)} loading`}
                    aria-label="Loading status data"
                >
                    <div className="status-count loading-placeholder"></div>
                    <div className="status-label loading-placeholder"></div>
                    <div className="status-percentage loading-placeholder"></div>
                </output>
            );
        }

        return (
            <OverlayTrigger
                key={status}
                placement="top"
                overlay={
                    <Tooltip id={`tooltip-${status}`}>
                        {STATUS_DESCRIPTIONS[status]}
                    </Tooltip>
                }
            >
                <output 
                    className={`status-summary-item status-${getStatusGroup(status)}`}
                    aria-label={`${READABLE_SURVEY_INPUT_PROCESSING_STATES[status]}: ${statusCounts[status]} records (${getStatusPercentage(statusCounts[status])}%)`}
                >
                    <div className="status-count">{statusCounts[status]}</div>
                    <div className="status-label">{READABLE_SURVEY_INPUT_PROCESSING_STATES[status]}</div>
                    <div className="status-percentage">{getStatusPercentage(statusCounts[status])}%</div>
                </output>
            </OverlayTrigger>
        );
    };

    return (
        <section className="status-summary" aria-label="Status distribution summary">
            <h3>Status Overview</h3>
            <div className="status-summary-grid">
                {STATUS_WORKFLOW_ORDER.map(status => renderStatusCard(status))}
            </div>
        </section>
    );
};

StatusSummary.propTypes = {
    data: PropTypes.arrayOf(PropTypes.shape({
        processingStatus: PropTypes.string.isRequired
    })),
    loading: PropTypes.bool
};

export default StatusSummary; 